import config from '../../config';
import { authHeader } from '../_helpers/auth-header';
import { createRequestCache } from '../Page/HomePage/floorDeckRequestCache';
import { DEFAULT_AVAILABLE_HOURS } from '../utils/utilizationTime';

// Company-wide Floor Deck utilization settings: the machine categories counted,
// the machines left out, and the scheduled available hours per machine per day.
const settingsCache = createRequestCache(60 * 1000);
const machinesCache = createRequestCache(60 * 1000);

async function readJson(response, fallbackMessage) {
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        const error = new Error((data && (data.error || data.message)) || fallbackMessage);
        error.status = response.status;
        throw error;
    }

    return data;
}

async function requestSettings(method, body) {
    const response = await fetch(`${config.apiUrl}/utilization-settings`, {
        method,
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const data = await readJson(response, 'Unable to load utilization settings.');
    const hours = Number(data.availableHours);

    return {
        categories: Array.isArray(data.categories) ? data.categories : [],
        availableHours: hours > 0 ? hours : DEFAULT_AVAILABLE_HOURS,
        excludedMachines: Array.isArray(data.excludedMachines) ? data.excludedMachines : [],
    };
}

// The server reads the company from the credentials, so key the cache by them:
// a different signed-in user never sees another company's selection.
function settingsKey() {
    return authHeader().Authorization || '';
}

function getSettings() {
    return settingsCache.getStaleWhileRevalidate(settingsKey(), () => requestSettings('GET'));
}

async function saveSettings({ categories, availableHours, excludedMachines }) {
    const key = settingsKey();
    settingsCache.clear();

    try {
        const saved = await requestSettings('PUT', { categories, availableHours, excludedMachines });
        // Seed the cache with the confirmed value so the Floor Deck shows it at once.
        settingsCache.get(key, () => saved, true);
        return saved;
    } catch (error) {
        settingsCache.clear();
        throw error;
    }
}

function getMachines(company) {
    const loader = () => fetch(`${config.apiUrl}/getAllMachines?company=${encodeURIComponent(company)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then((response) => readJson(response, 'Unable to load machines.'))
        .then((rows) => (Array.isArray(rows) ? rows : []));

    return machinesCache.getStaleWhileRevalidate(String(company), loader);
}

export const utilizationSettingsService = {
    getSettings,
    saveSettings,
    getMachines,
    clear: () => {
        settingsCache.clear();
        machinesCache.clear();
    },
};
